import { z } from "zod";
import { EXERCISE_GOAL_TARGET_MAX_KG, exerciseGoalMetric, exercisePreference, } from "../training/exercise-personal";
import { agentWriteRefusal, CATALOGUE_LOCALES, } from "./data-access";
/**
 * What the person said about their lifts, for the trainer (`D228`, extended):
 * goals, prefer/avoid/favourite, notes, strength levels (`D200`, `D208`),
 * muscle readiness (`D203`), and the saved gym in use (`D202`).
 *
 * One read and four writes. Every write goes through the use case the app's
 * own screen calls, stamped `agent` on the far side (`D69`), so the row says
 * "you, through the assistant" — and every one runs inside the turn's
 * write-once ledger (`T-154`).
 *
 * ── THE SAME RULE AS EVERY SCHEMA ON THIS CHANNEL (`D72`) ───────────────────
 * No field names a person. `useGym` takes a gym id, which the far side looks
 * up among the CALLER's own gyms and nowhere else (`D202`'s `/me/` rule), so
 * an id from anybody else is a refusal; `removeExerciseGoal` takes the
 * exercise, not a goal id, and removes the subject's goal on it.
 */
/** `getExerciseData` takes the locale names come back in, and nothing else. */
export const getExerciseDataRequest = z.strictObject({
    locale: z.enum(CATALOGUE_LOCALES).optional(),
});
/** A catalogue exercise id, as every plan tool takes it. */
const exerciseId = z.string().min(1).max(64);
export const setExerciseGoalRequest = z.strictObject({
    /** `YYYY-MM-DD`, or omitted for no date. */
    deadline: z.iso.date().optional(),
    exerciseId,
    metric: exerciseGoalMetric.describe("`e1rm` — the estimated one-rep max; `load` — the heaviest load lifted."),
    targetKg: z.number().positive().max(EXERCISE_GOAL_TARGET_MAX_KG),
});
export const removeExerciseGoalRequest = z.strictObject({
    exerciseId,
    /** Omitted removes every live goal on the exercise. */
    metric: exerciseGoalMetric.optional(),
});
export const setExercisePreferenceRequest = z
    .strictObject({
    exerciseId,
    favorite: z.boolean().optional(),
    /** `null` clears it; omitted leaves it as it is. */
    preference: exercisePreference.nullable().optional(),
})
    .refine((value) => value.favorite !== undefined || value.preference !== undefined, { message: "set `favorite`, `preference`, or both" });
export const useGymRequest = z.strictObject({
    /** A gym's `id` from `getTrainingSetup` or `getPlanningContext`. */
    gymId: z.string().min(1).max(64),
});
export const agentExerciseGoalWriteResult = z.discriminatedUnion("ok", [
    z.object({
        achievedAt: z.string().nullable(),
        /** The best already logged on that metric, or `null`. */
        bestKg: z.number().nullable(),
        exerciseId: z.string(),
        metric: exerciseGoalMetric,
        ok: z.literal(true),
        targetKg: z.number(),
    }),
    agentWriteRefusal,
]);
export const agentExerciseGoalRemoveResult = z.discriminatedUnion("ok", [
    z.object({ ok: z.literal(true), removed: z.int().nonnegative() }),
    agentWriteRefusal,
]);
export const agentExercisePreferenceWriteResult = z.discriminatedUnion("ok", [
    z.object({
        exerciseId: z.string(),
        favorite: z.boolean(),
        ok: z.literal(true),
        preference: exercisePreference.nullable(),
    }),
    agentWriteRefusal,
]);
export const agentGymSwitchResult = z.discriminatedUnion("ok", [
    z.object({
        /** How many pieces of equipment are now in effect. */
        availableCount: z.number(),
        name: z.string(),
        ok: z.literal(true),
    }),
    agentWriteRefusal,
]);
//# sourceMappingURL=exercise-data-tools.js.map